/**
 * 端末ID⇄編成番号の紐づけ一覧ページ (SBI-5)。
 *
 * 現場 WG の WebApp 上で、DeviceRegistry テーブルに登録されている
 * 「端末ID (device_id) と編成番号 (vehicle_id) の最新紐づけ + 動作ステータス」を
 * 一覧確認する用途。
 *
 * 表示列:
 *   - 編成番号 (vehicle_id)
 *   - 端末ID (device_id)
 *   - 端末名 (device_name。任意入力。セル内の鉛筆アイコンボタンから
 *     インライン編集で設定・解除できる。Issue #360)
 *   - 種別 (Web / Android。device_id プレフィックスからフロント側で導出)
 *   - 最終登録日時 (last_registered_at)
 *   - 現在の列番 (current_trip_id。VehicleTripMapping 由来。在線情報が無い場合は "—")
 *   - ステータス (正常=緑バッジ / 異常=赤バッジ + last_alert_sent_at ツールチップ)
 *   - 状態更新時刻 (status_updated_at)
 *
 * フィルタ:
 *   - 「異常のみ表示」トグル (status === 'abnormal' の行のみ表示)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/broadcastPoints';
import { deviceTypeFromId, DeviceRegistrationsClient } from '../api/deviceRegistrations';
import type { DeviceRegistration } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { formatDateTime } from '../utils/dateTime';

interface DeviceRegistrationListPageProps {
  config: AppConfig;
}

/**
 * 現在の列番が解決できない場合の表示。
 *
 * サーバ側 (`GET /device-registrations`) が `current_trip_id: null` を返すのは
 * 「当該編成が在線していない (直近 5 分以内に在線情報が更新されていない)」場合、
 * または VehicleTripMapping の参照に失敗した場合 (フェイルセーフ)。
 */
const CURRENT_TRIP_EMPTY = '—';

/** `current_trip_id` が null のときにツールチップで表示する理由。 */
const CURRENT_TRIP_EMPTY_REASON = '在線情報がありません';

/**
 * 端末名 (`device_name`) が未設定のときの表示 (Issue #360)。
 *
 * 「まだ名前を付けていない」ことが分かるようにする。空欄だと列がずれて見えるため
 * `current_trip_id` と同じダッシ記号でプレースホルダを置く。
 */
const DEVICE_NAME_EMPTY = '—';

/**
 * 端末名の最大長。サーバ側 (`MAX_DEVICE_NAME_LENGTH` in
 * `lambda/control-panel-api/index.py`) と必ず一致させること。
 * 不一致だと入力できたのに 400 で弾かれるという分かりにくい挙動になる。
 */
const MAX_DEVICE_NAME_LENGTH = 100;

/** 端末名インライン編集中の状態 (Issue #360)。 */
interface DeviceNameEditState {
  /** 編集対象の編成番号 (DeviceRegistry の PK) */
  vehicleId: string;
  /** 編集開始時の端末名 (変更有無の判定に使う。未設定なら空文字) */
  originalDeviceName: string;
  /** 入力中の端末名 */
  deviceName: string;
  /** 保存処理中フラグ (二重送信防止) */
  saving: boolean;
}

export function DeviceRegistrationListPage(
  props: DeviceRegistrationListPageProps,
): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new DeviceRegistrationsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [items, setItems] = useState<DeviceRegistration[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [abnormalOnly, setAbnormalOnly] = useState(false);
  const requestIdRef = useRef(0);

  // --- 端末名のインライン編集 (Issue #360) ---
  const [nameEdit, setNameEdit] = useState<DeviceNameEditState | null>(null);

  /**
   * DeviceRegistry の一覧を取得して state に反映する。
   *
   * `requestIdRef` を用いて古いリクエスト / アンマウント後の結果を破棄し、
   * 競合状態 (race condition) を防ぐ。
   */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      // 古いリクエストまたはアンマウント後は無視
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setItems(res.items);
      setTotalCount(res.count);
    } catch (err) {
      // 古いリクエストまたはアンマウント後は無視
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setErrorMessage(formatApiError(err));
    } finally {
      // アンマウント後や古いリクエストは無視
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  // 初回マウント時 (および apiClient の再生成時) に一覧を取得する。
  // React 18 StrictMode 開発環境では effect が 2 回実行されるが、
  // `requestIdRef` による競合防止で最新の結果のみが反映される。
  // cleanup で requestId をインクリメントすることで、進行中の古いリクエスト結果を
  // 全て無視させ、アンマウント後の setState も防ぐ。
  // reload は先頭で setLoading(true) 等の setState を同期実行するため
  // react-hooks/set-state-in-effect が警告するが、これは外部システム (API) から
  // データを取り込む正当なユースケースであり意図的なものなので抑制する。
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- API 取得の初回ロードは正当な副作用
    void reload();

    // アンマウント時に requestId をインクリメントして、
    // 進行中のリクエスト結果を全て無視させ、二重実行の影響も防ぐ。
    // ここでは ref の「現在値を読む」のではなく単にインクリメントするだけなので、
    // exhaustive-deps の「cleanup 時に ref が変わっている可能性」警告は該当しない。
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref はカウンタ用途で、cleanup での加算は意図的
      ++requestIdRef.current;
    };
  }, [reload]);

  const visibleItems = useMemo(
    () => (abnormalOnly ? items.filter((i) => i.status === 'abnormal') : items),
    [items, abnormalOnly],
  );

  /** 指定行の端末名編集を開始する (Issue #360)。 */
  const startNameEdit = useCallback((item: DeviceRegistration): void => {
    setErrorMessage(null);
    setNameEdit({
      vehicleId: item.vehicle_id,
      originalDeviceName: item.device_name ?? '',
      deviceName: item.device_name ?? '',
      saving: false,
    });
  }, []);

  /** 端末名編集をキャンセルする (入力を破棄)。 */
  const cancelNameEdit = useCallback((): void => {
    setNameEdit(null);
  }, []);

  /**
   * 端末名を保存する (Issue #360)。
   *
   * 変更が無い場合は API を呼ばずに編集を閉じる (無駄な書き込みを避ける)。
   * 保存に成功したら該当行だけをサーバの応答で差し替える。全件 reload しないのは、
   * 「異常のみ表示」フィルタやスクロール位置を保ったまま更新するため。
   */
  const saveNameEdit = useCallback(async (): Promise<void> => {
    if (nameEdit === null || nameEdit.saving) return;
    const target = nameEdit;
    const trimmed = target.deviceName.trim();
    if (trimmed === target.originalDeviceName.trim()) {
      setNameEdit(null);
      return;
    }
    setNameEdit({ ...target, saving: true });
    setErrorMessage(null);
    try {
      const updated = await apiClient.updateDeviceName(target.vehicleId, trimmed);
      setItems((prev) =>
        prev.map((item) =>
          item.vehicle_id === target.vehicleId
            ? // サーバ応答には current_trip_id が含まれない (一覧取得専用の合成値) ため、
              // 表示中の値を保持しつつ DeviceRegistry 由来の属性のみを差し替える。
              { ...item, ...updated, current_trip_id: item.current_trip_id }
            : item,
        ),
      );
      setNameEdit(null);
    } catch (err) {
      // 失敗時は入力内容を保持したまま編集を継続させ、再試行できるようにする。
      setErrorMessage(formatApiError(err));
      setNameEdit((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  }, [apiClient, nameEdit]);

  return (
    <section className="section" data-testid="device-list-section">
      <h2>
        端末登録一覧{' '}
        {loading
          ? '(読み込み中...)'
          : visibleItems.length === items.length
            ? `(${totalCount} 件)`
            : `(${visibleItems.length} / ${totalCount} 件)`}
      </h2>

      {errorMessage ? <div className="error-message">{errorMessage}</div> : null}

      <div className="sort-filter-bar">
        <label className="sort-filter-radio">
          <input
            type="checkbox"
            checked={abnormalOnly}
            onChange={(e) => setAbnormalOnly(e.target.checked)}
            data-testid="abnormal-only-toggle"
          />
          異常のみ表示
        </label>
        <button type="button" onClick={() => void reload()} data-testid="device-reload-button">
          再読み込み
        </button>
      </div>

      <div className="table-wrapper">
        <table className="data-table" data-testid="device-table">
          <thead>
            <tr>
              <th scope="col">編成番号</th>
              <th scope="col">端末ID</th>
              {/* Issue #360: 端末名 (任意入力)。行ごとに鉛筆アイコンからインライン編集できる。 */}
              <th scope="col">端末名</th>
              <th scope="col">種別</th>
              <th scope="col">最終登録日時</th>
              <th scope="col">現在の列番</th>
              <th scope="col">ステータス</th>
              <th scope="col">状態更新時刻</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 ? (
              <tr>
                <td colSpan={8} className="data-table__empty">
                  {loading ? '読み込み中...' : '該当する端末がありません'}
                </td>
              </tr>
            ) : (
              visibleItems.map((item) => {
                const isEditingName = nameEdit !== null && nameEdit.vehicleId === item.vehicle_id;
                return (
                  <tr key={item.vehicle_id} data-testid={`device-row-${item.vehicle_id}`}>
                    <td>{item.vehicle_id}</td>
                    <td>{item.device_id}</td>
                    {/*
                      Issue #360: 端末名 (任意入力)。
                      表示中は「端末名 + 鉛筆アイコンボタン」で、鉛筆を押すと同じセル内が
                      テキスト入力 + 保存 / キャンセルに切り替わる (インライン編集)。
                    */}
                    <td>
                      <div className="device-name-cell">
                        {isEditingName && nameEdit ? (
                          <>
                            <input
                              type="text"
                              className="device-name-cell__input"
                              value={nameEdit.deviceName}
                              maxLength={MAX_DEVICE_NAME_LENGTH}
                              onChange={(e) =>
                                setNameEdit((prev) =>
                                  prev ? { ...prev, deviceName: e.target.value } : prev,
                                )
                              }
                              onKeyDown={(e) => {
                                // Enter で保存 / Escape でキャンセル (表形式の編集で一般的な操作)
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void saveNameEdit();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  cancelNameEdit();
                                }
                              }}
                              disabled={nameEdit.saving}
                              aria-label={`${item.vehicle_id} の端末名`}
                              placeholder="端末名 (任意)"
                              // 鉛筆ボタンから編集に入った直後に入力できるようフォーカスを当てる
                              autoFocus
                              data-testid={`device-name-input-${item.vehicle_id}`}
                            />
                            <button
                              type="button"
                              className="icon-button icon-button--primary"
                              onClick={() => void saveNameEdit()}
                              disabled={nameEdit.saving}
                              title="端末名を保存"
                              aria-label={`${item.vehicle_id} の端末名を保存`}
                              data-testid={`device-name-save-${item.vehicle_id}`}
                            >
                              <CheckIcon />
                            </button>
                            <button
                              type="button"
                              className="icon-button"
                              onClick={cancelNameEdit}
                              disabled={nameEdit.saving}
                              title="端末名の編集をキャンセル"
                              aria-label={`${item.vehicle_id} の端末名の編集をキャンセル`}
                              data-testid={`device-name-cancel-${item.vehicle_id}`}
                            >
                              <CloseIcon />
                            </button>
                          </>
                        ) : (
                          <>
                            {item.device_name ? (
                              <span data-testid={`device-name-${item.vehicle_id}`}>
                                {item.device_name}
                              </span>
                            ) : (
                              <span
                                title="端末名は未設定です"
                                data-testid={`device-name-empty-${item.vehicle_id}`}
                              >
                                {DEVICE_NAME_EMPTY}
                                {/* 記号だけでは読み上げで意味が伝わらないため補助テキストを添える */}
                                <span className="visually-hidden">（端末名は未設定です）</span>
                              </span>
                            )}
                            {/*
                              鉛筆アイコンボタン。アイコンだけでは読み上げで意味が伝わらないため
                              aria-label を、マウス利用者には title でツールチップを与える。
                            */}
                            <button
                              type="button"
                              className="icon-button icon-button--edit"
                              onClick={() => startNameEdit(item)}
                              // 別の行を編集中は誤操作を防ぐため無効化する
                              disabled={nameEdit !== null}
                              title="端末名を編集"
                              aria-label={`${item.vehicle_id} の端末名を編集`}
                              data-testid={`device-name-edit-${item.vehicle_id}`}
                            >
                              <PencilIcon />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td>{deviceTypeFromId(item.device_id)}</td>
                    <td>{formatDateTime(item.last_registered_at)}</td>
                    <td>
                      {item.current_trip_id ? (
                        <span data-testid="current-trip-id">{item.current_trip_id}</span>
                      ) : (
                        <span title={CURRENT_TRIP_EMPTY_REASON} data-testid="current-trip-empty">
                          {CURRENT_TRIP_EMPTY}
                          {/*
                          記号 "—" だけでは読み上げで理由が伝わらないため、
                          スクリーンリーダー専用テキストで補う (視覚上は非表示)。
                          マウス利用者には title 属性のツールチップで同じ情報を提供する。
                        */}
                          <span className="visually-hidden">（{CURRENT_TRIP_EMPTY_REASON}）</span>
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={item.status} lastAlertSentAt={item.last_alert_sent_at} />
                    </td>
                    <td>{formatDateTime(item.status_updated_at)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface StatusBadgeProps {
  status: DeviceRegistration['status'];
  lastAlertSentAt: string | null;
}

/**
 * ステータスバッジ。
 * - `normal`: 緑バッジ「正常」
 * - `abnormal`: 赤バッジ「異常」+ last_alert_sent_at をツールチップ (title) 表示
 * - その他 / null: 中立バッジ「不明」
 */
function StatusBadge({ status, lastAlertSentAt }: StatusBadgeProps): React.JSX.Element {
  if (status === 'normal') {
    return (
      <span
        className="status-badge status-badge--normal"
        aria-label="ステータス: 正常"
        data-testid="status-badge"
      >
        正常
      </span>
    );
  }
  if (status === 'abnormal') {
    const tooltip = lastAlertSentAt
      ? `最終アラート送信: ${formatDateTime(lastAlertSentAt)}`
      : 'アラート送信履歴なし';
    return (
      <span
        className="status-badge status-badge--abnormal"
        title={tooltip}
        aria-label={`ステータス: 異常。${tooltip}`}
        tabIndex={0}
        data-testid="status-badge"
      >
        異常
      </span>
    );
  }
  return (
    <span
      className="status-badge status-badge--unknown"
      aria-label="ステータス: 不明"
      data-testid="status-badge"
    >
      不明
    </span>
  );
}

/*
 * -----------------------------------------------------------------------------
 * アイコン (Issue #360)
 *
 * 端末名セルの操作ボタンで使うアイコン。外部アイコンライブラリを増やさずに済むよう
 * インライン SVG で定義する。いずれも `aria-hidden` + `focusable="false"` として
 * 読み上げ対象から外し、意味はボタン側の `aria-label` / `title` で伝える
 * (アイコンが二重に読み上げられるのを防ぐため)。
 * `currentColor` を使うことでボタンの hover / disabled 時の色変化に追従する。
 * -----------------------------------------------------------------------------
 */

/** 鉛筆 (編集) アイコン。端末名の編集開始ボタンで使う。 */
function PencilIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-testid="pencil-icon"
    >
      {/* 鉛筆の軸 */}
      <path d="M11.2 1.8a1.4 1.4 0 0 1 2 2L5.4 11.6l-2.9.8.8-2.9 7.9-7.7Z" />
      {/* 鉛筆の先端 (刃の境界線) */}
      <path d="M10 3l3 3" />
    </svg>
  );
}

/** チェック (保存) アイコン。 */
function CheckIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-testid="check-icon"
    >
      <path d="M3 8.5 6.2 12 13 4.5" />
    </svg>
  );
}

/** × (キャンセル) アイコン。 */
function CloseIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-testid="close-icon"
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

/** API クライアントの例外を画面表示用文字列に整形する */
function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return `[HTTP ${err.status}] ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return '予期しないエラーが発生しました';
}
